import React, { useState, useEffect } from 'react';
import CustomSelect from '@/components/common/CustomSelect';
import * as mecanicosApiService from '@/services/mecanicosApiService';
import * as sucursalesApiService from '@/services/sucursalesApiService';
import * as categoriasApiService from '@/services/categoriasApiService';
import { useSucursal } from '@/context/SucursalContext';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import FilterBar from '@/components/common/FilterBar';
import CustomCheckbox from '@/components/common/CustomCheckbox';
import CheckboxFiltersContainer from '@/components/common/CheckboxFiltersContainer';
import MecanicosList from './MecanicosList';
import MecanicoForm from './MecanicoForm';
import { FaPlus, FaFilter, FaSearch, FaUserCog, FaMapMarkerAlt } from 'react-icons/fa';
import toastService from '@/utils/toastService';

/**
 * Componente principal para la gestión de mecánicos
 * 
 * @returns {JSX.Element} - Componente de mantenedor de mecánicos
 */
const MecanicosMantenedor = () => {
  const { selectedSucursal } = useSucursal();
  const [mecanicos, setMecanicos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [filteredMecanicos, setFilteredMecanicos] = useState([]);
  const [selectedMecanico, setSelectedMecanico] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false); // Comentado - funcionalidad futura
  const [loading, setLoading] = useState(false);
  
  // Estado para detectar si estamos en móvil
  const [isMobile, setIsMobile] = useState(false);
  
  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Comprobar inicialmente
    checkMobile();
    
    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Filtros
  const [filters, setFilters] = useState({
    especialidad: '',
    searchTerm: '',
    mostrarInactivos: false
  });
  
  // Cargar especialidades desde la API
  const fetchEspecialidades = async () => {
    try {
      const especialidadesData = await categoriasApiService.getCategoriasByTipo('especialidad_mecanico');
      setEspecialidades(especialidadesData);
    } catch (error) {
      console.error('Error al cargar especialidades:', error);
      toastService.error('Error al cargar especialidades');
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    fetchEspecialidades();
  }, []);

  // Cargar mecánicos y sucursales al iniciar y cuando cambie la sucursal
  useEffect(() => {
    fetchData();
  }, [selectedSucursal]);
  
  // Aplicar filtros cuando cambian
  useEffect(() => {
    applyFilters();
  }, [mecanicos, filters]);

  // Función para cargar datos desde la API
  const fetchData = async () => {
    setLoading(true);
    try {
      // Cargar mecánicos filtrados por sucursal
      const mecanicosData = await mecanicosApiService.getMecanicos(selectedSucursal);
      setMecanicos(mecanicosData);
      
      // Cargar sucursales
      const sucursalesData = await sucursalesApiService.getSucursales();
      setSucursales(sucursalesData);
    } catch (error) {
      console.error("Error al cargar datos:", error);
      toastService.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros a la lista de mecánicos
  const applyFilters = () => {
    let filtered = [...mecanicos];
    
    // Filtrar por especialidad
    if (filters.especialidad) {
      filtered = filtered.filter(m => {
        // Buscar por categoria_id o por nombre de especialidad
        const especialidad = especialidades.find(esp => esp.id.toString() === filters.especialidad);
        return m.categoria_id?.toString() === filters.especialidad || 
               m.Especialidad === filters.especialidad ||
               (especialidad && m.Especialidad === especialidad.nombre);
      });
    }
    
    // Filtrar por término de búsqueda
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(m => 
        m.Nombre.toLowerCase().includes(term) || 
        (m.Email && m.Email.toLowerCase().includes(term))
      );
    }
    
    // Filtrar por estado activo/inactivo
    if (!filters.mostrarInactivos) {
      filtered = filtered.filter(m => m.activo === true);
    }
    
    setFilteredMecanicos(filtered);
  };


  // Manejar cambios en los filtros
  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Abrir modal para agregar o editar mecánico
  const handleOpenModal = (mecanico = null) => {
    setSelectedMecanico(mecanico);
    setIsModalOpen(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMecanico(null);
  };

  // FUNCIONALIDAD DE HORARIOS COMENTADA - PARA USO FUTURO
  // // Abrir modal para gestionar horario
  // const handleOpenScheduleModal = (mecanico) => {
  //   setSelectedMecanico(mecanico);
  //   setIsScheduleModalOpen(true);
  // };

  // // Cerrar modal de horario
  // const handleCloseScheduleModal = () => {
  //   setIsScheduleModalOpen(false);
  //   setSelectedMecanico(null);
  // };

  // Manejar envío del formulario
  const handleSubmitForm = async (formData) => {
    try {
      if (selectedMecanico) {
        // Actualizar mecánico existente
        await mecanicosApiService.updateMecanico(selectedMecanico.id, formData);
        toastService.success("Mecánico actualizado correctamente");
      } else {
        // Crear nuevo mecánico
        await mecanicosApiService.createMecanico(formData);
        toastService.success("Mecánico creado correctamente");
      }
      
      // Recargar la lista de mecánicos
      fetchData();
      
      // Cerrar el modal
      handleCloseModal();
    } catch (error) {
      console.error("Error al guardar mecánico:", error);
      toastService.error("Error al guardar el mecánico");
    }
  };

  // Manejar cambio de estado activo/inactivo
  const handleToggleActive = async (id, newStatus) => {
    try {
      await mecanicosApiService.toggleMecanicoActive(id, newStatus);
      toastService.success(`Mecánico ${newStatus ? 'activado' : 'desactivado'} correctamente`);
      fetchData(); // Recargar la lista de mecánicos
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      toastService.error("Error al cambiar el estado del mecánico");
    }
  };

  // Manejar eliminación de mecánico
  const handleDeleteMecanico = async (id) => {
    try {
      await mecanicosApiService.deleteMecanico(id);
      // Recargar la lista de mecánicos
      fetchData();
      toastService.success("Mecánico eliminado correctamente");
    } catch (error) {
      console.error("Error al eliminar mecánico:", error);
      toastService.error("Error al eliminar el mecánico");
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Mecánicos</h1>
        
        <Button
          variant="primary"
          onClick={() => handleOpenModal()}
          icon={<FaPlus />}
        >
          Agregar Mecánico
        </Button>
      </div>
      
      {/* Filtros con el componente FilterBar mejorado */}
      <FilterBar 
        onSearch={(value) => setFilters({...filters, searchTerm: value})}
        searchValue={filters.searchTerm}
        onReset={() => setFilters({especialidad: '', searchTerm: '', mostrarInactivos: false})}
        searchPlaceholder="Buscar mecánicos..."
        checkboxFilters={
          <CheckboxFiltersContainer>
            <CustomCheckbox
              checked={filters.mostrarInactivos}
              onChange={(checked) => {
                setFilters(prev => ({
                  ...prev,
                  mostrarInactivos: checked
                }));
              }}
              label="Incluir inactivos"
              id="mostrarInactivos"
              name="mostrarInactivos"
            />
          </CheckboxFiltersContainer>
        }
      >
        {/* Filtro por especialidad - CustomSelect para consistencia en altura */}
        <CustomSelect
          name="especialidad"
          value={filters.especialidad}
          onChange={handleFilterChange}
          className="w-full md:min-w-[200px] md:w-auto bg-gray-700 border border-gray-600 rounded text-white"
          placeholder="Todas las especialidades"
          options={especialidades
            .sort((a, b) => a.nombre.localeCompare(b.nombre))
            .map(esp => ({
              id: esp.id,
              value: esp.id.toString(),
              label: esp.nombre
            }))}
        />
      </FilterBar>

      {/* Toggles de especialidad para móvil */}
      {isMobile && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilters({...filters, especialidad: ''})}
              className={`px-3 py-1 rounded-full text-sm transition-all ${
                filters.especialidad === '' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Todas
            </button>
            {especialidades
              .sort((a, b) => a.nombre.localeCompare(b.nombre))
              .map(esp => (
                <button
                  key={esp.id}
                  onClick={() => setFilters({...filters, especialidad: esp.id.toString()})}
                  className={`px-3 py-1 rounded-full text-sm transition-all ${
                    filters.especialidad === esp.id.toString() 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {esp.nombre}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mostrar cargando o lista de mecánicos */}
      {loading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <MecanicosList
          mecanicos={filteredMecanicos}
          sucursales={sucursales}
          especialidades={especialidades}
          onEdit={handleOpenModal}
          onSchedule={null} // Comentado - funcionalidad futura: handleOpenScheduleModal
          onToggleActive={handleToggleActive}
          onDelete={handleDeleteMecanico}
        />
      )}

      {/* Modal para agregar o editar mecánico */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedMecanico ? 'Editar Mecánico' : 'Agregar Mecánico'}
        size="2xl"
        headerExtra={selectedSucursal && (
          <div className="bg-blue-900/30 px-3 py-2 rounded-lg border border-blue-800/50">
            <p className="text-blue-200 text-sm flex items-center">
              <FaMapMarkerAlt className="text-blue-400 mr-2 text-sm" />
              <strong>{sucursales.find(s => s.id === selectedSucursal)?.nombre || sucursales.find(s => s.id === selectedSucursal)?.Nombre || 'Desconocida'}</strong>
            </p>
          </div>
        )}
      >
        <MecanicoForm
          initialData={selectedMecanico || {}}
          sucursales={sucursales}
          especialidades={especialidades}
          onSubmit={handleSubmitForm}
          onCancel={handleCloseModal}
          onStatusChange={fetchData}
        />
      </Modal>
      
      {/* MODAL DE HORARIOS COMENTADO - FUNCIONALIDAD FUTURA */}
      {/* <Modal
        isOpen={isScheduleModalOpen}
        onClose={handleCloseScheduleModal}
        title="Gestionar Horario"
        size="lg"
      >
        <MecanicoScheduleForm
          mecanico={selectedMecanico}
          onSave={() => {
            toastService.success("Horario guardado correctamente");
            handleCloseScheduleModal();
          }}
          onCancel={handleCloseScheduleModal}
        />
      </Modal> */}
    </div>
  );
};

/* COMPONENTE DE GESTIÓN DE HORARIOS COMENTADO - FUNCIONALIDAD FUTURA

// Componente para gestionar el horario de un mecánico
const MecanicoScheduleForm = ({ mecanico, onSave, onCancel }) => {
  // Datos simplificados para prototipo - en una implementación real obtendríamos los datos de la API
  const [schedule, setSchedule] = useState({
    lunes: { inicio: '08:00', fin: '18:00', activo: true },
    martes: { inicio: '08:00', fin: '18:00', activo: true },
    miercoles: { inicio: '08:00', fin: '18:00', activo: true },
    jueves: { inicio: '08:00', fin: '18:00', activo: true },
    viernes: { inicio: '08:00', fin: '18:00', activo: true },
    sabado: { inicio: '09:00', fin: '14:00', activo: true },
    domingo: { inicio: '00:00', fin: '00:00', activo: false }
  });
  
  const [loading, setLoading] = useState(false);
  
  if (!mecanico) return null;
  
  const dias = [
    { id: 'lunes', label: 'Lunes' },
    { id: 'martes', label: 'Martes' },
    { id: 'miercoles', label: 'Miércoles' },
    { id: 'jueves', label: 'Jueves' },
    { id: 'viernes', label: 'Viernes' },
    { id: 'sabado', label: 'Sábado' },
    { id: 'domingo', label: 'Domingo' }
  ];
  
  const handleScheduleChange = (dia, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [dia]: {
        ...prev[dia],
        [field]: field === 'activo' ? value === 'true' : value
      }
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simular guardado (en una implementación real, enviaríamos los datos a la API)
    setTimeout(() => {
      setLoading(false);
      onSave();
    }, 1000);
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-4">
        <h3 className="font-medium text-lg">{mecanico.Nombre}</h3>
        <p className="text-sm text-gray-400">{mecanico.Especialidad} - Sucursal {mecanico.SucursalNombre}</p>
      </div>
      
      <div className="bg-gray-750 rounded-lg border border-gray-700 overflow-hidden mb-4">
        <table className="w-full">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-4 py-2 text-left">Día</th>
              <th className="px-4 py-2 text-left">Activo</th>
              <th className="px-4 py-2 text-left">Hora de inicio</th>
              <th className="px-4 py-2 text-left">Hora de fin</th>
            </tr>
          </thead>
          <tbody>
            {dias.map(dia => (
              <tr key={dia.id} className="border-t border-gray-700">
                <td className="px-4 py-3">{dia.label}</td>
                <td className="px-4 py-3">
                  <select
                    value={schedule[dia.id].activo.toString()}
                    onChange={(e) => handleScheduleChange(dia.id, 'activo', e.target.value)}
                    className="p-1 bg-gray-700 border border-gray-600 rounded text-white"
                  >
                    <option value="true">Sí</option>
                    <option value="false">No</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="time"
                    value={schedule[dia.id].inicio}
                    onChange={(e) => handleScheduleChange(dia.id, 'inicio', e.target.value)}
                    disabled={!schedule[dia.id].activo}
                    className="p-1 bg-gray-700 border border-gray-600 rounded text-white"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="time"
                    value={schedule[dia.id].fin}
                    onChange={(e) => handleScheduleChange(dia.id, 'fin', e.target.value)}
                    disabled={!schedule[dia.id].activo}
                    className="p-1 bg-gray-700 border border-gray-600 rounded text-white"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="flex justify-end space-x-2">
        <Button
          variant="secondary"
          onClick={onCancel}
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={loading}
        >
          Guardar Horario
        </Button>
      </div>
    </form>
  );
};

FUNCIONALIDAD FUTURA: Para activar, descomenta este componente y las funciones relacionadas arriba */

export default MecanicosMantenedor;